import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { TreePine, Heart, Plus, Trash2, User, MapPin, Upload, X, Share2, FileDown, Mail, Edit2, ArrowLeft, Pencil, Eye, ChevronDown, Settings, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Color palette for family units
const FAMILY_COLORS = [
  { bg: 'bg-emerald-50', border: 'border-emerald-300', text: 'text-emerald-700' },
  { bg: 'bg-sky-50', border: 'border-sky-300', text: 'text-sky-700' },
  { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-700' },
  { bg: 'bg-rose-50', border: 'border-rose-300', text: 'text-rose-700' },
  { bg: 'bg-violet-50', border: 'border-violet-300', text: 'text-violet-700' },
  { bg: 'bg-teal-50', border: 'border-teal-300', text: 'text-teal-700' },
  { bg: 'bg-orange-50', border: 'border-orange-300', text: 'text-orange-700' },
  { bg: 'bg-indigo-50', border: 'border-indigo-300', text: 'text-indigo-700' },
];

// Relation types categorized
const SPOUSE_RELATIONS = ['Spouse', 'Wife', 'Husband'];
const SIBLING_RELATIONS = ['Brother', 'Sister', 'Sibling'];
const PARENT_RELATIONS = ['Father', 'Mother', 'Parent', 'Guardian'];
const CHILD_RELATIONS = ['Son', 'Daughter', 'Child'];

export default function FamilyTreeBuilder({ user }) {
  const navigate = useNavigate();
  const { treeId } = useParams();
  const [loading, setLoading] = useState(true);
  const [trees, setTrees] = useState([]);
  const [currentTree, setCurrentTree] = useState(null);
  const [showTreeList, setShowTreeList] = useState(!treeId);
  const [showAddPerson, setShowAddPerson] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [addRelationType, setAddRelationType] = useState(null); // 'parent', 'spouse', 'sibling', 'child'
  const [showNameModal, setShowNameModal] = useState(false);
  const [isEditingTreeName, setIsEditingTreeName] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [treeName, setTreeName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [isEditingPerson, setIsEditingPerson] = useState(false);
  const [editingNodeId, setEditingNodeId] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  
  const [newPerson, setNewPerson] = useState({
    name: '',
    location: '',
    photo: '',
    relation: ''
  });

  // Helper functions
  const isSpouseRelation = (relation) => SPOUSE_RELATIONS.includes(relation);
  const isSiblingRelation = (relation) => SIBLING_RELATIONS.includes(relation);
  const isParentRelation = (relation) => PARENT_RELATIONS.includes(relation);
  const isChildRelation = (relation) => CHILD_RELATIONS.includes(relation);

  useEffect(() => {
    fetchTrees();
  }, []);

  useEffect(() => {
    if (treeId) {
      fetchTree(treeId);
    }
  }, [treeId]);

  const fetchTrees = async () => {
    try {
      const response = await axios.get(`${API}/family-trees`, { withCredentials: true });
      setTrees(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching trees:', error);
      setLoading(false);
    }
  };

  const fetchTree = async (id) => {
    try {
      const response = await axios.get(`${API}/family-trees/${id}`, { withCredentials: true });
      setCurrentTree(response.data);
      setShowTreeList(false);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching tree:', error);
      toast.error('Failed to load tree');
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const createNewTree = async () => {
    if (!treeName.trim()) {
      toast.error('Please enter a tree name');
      return;
    }

    try {
      if (isEditingTreeName && currentTree) {
        await axios.put(`${API}/family-trees/${currentTree.tree_id}`, { name: treeName }, { withCredentials: true });
        setCurrentTree({ ...currentTree, name: treeName });
        setTrees(trees.map(t => t.tree_id === currentTree.tree_id ? { ...t, name: treeName } : t));
        setShowNameModal(false);
        setTreeName('');
        setIsEditingTreeName(false);
        toast.success('Tree name updated!');
      } else {
        const response = await axios.post(`${API}/family-trees`, { name: treeName }, { withCredentials: true });
        setCurrentTree(response.data);
        setTrees([...trees, response.data]);
        setShowNameModal(false);
        setShowTreeList(false);
        setTreeName('');
        setIsEditingTreeName(false);
        navigate(`/tree-builder/${response.data.tree_id}`);
        toast.success('Family tree created!');
      }
    } catch (error) {
      console.error('Error with tree:', error);
      toast.error(isEditingTreeName ? 'Failed to update tree' : 'Failed to create tree');
    }
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewPerson({ ...newPerson, photo: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const addPerson = async () => {
    if (!newPerson.name.trim()) {
      toast.error('Please enter a name');
      return;
    }

    try {
      // Determine branch_direction based on relation type
      let branchDirection = null;
      if (addRelationType === 'parent') branchDirection = 'top';
      else if (addRelationType === 'child') branchDirection = 'bottom';
      else if (addRelationType === 'spouse') branchDirection = 'right';
      else if (addRelationType === 'sibling') branchDirection = 'left';

      const personData = {
        tree_id: currentTree.tree_id,
        name: newPerson.name,
        location: newPerson.location || null,
        photo: newPerson.photo || null,
        parent_node_id: selectedNode?.node_id || null,
        branch_direction: branchDirection,
        color_index: selectedNode ? (selectedNode.color_index + 1) % FAMILY_COLORS.length : 0,
        relation: newPerson.relation || null
      };

      if (isEditingPerson && editingNodeId) {
        const response = await axios.put(
          `${API}/family-trees/${currentTree.tree_id}/nodes/${editingNodeId}`,
          personData,
          { withCredentials: true }
        );
        setCurrentTree({
          ...currentTree,
          nodes: currentTree.nodes.map(n => n.node_id === editingNodeId ? response.data : n)
        });
        toast.success(`${newPerson.name} updated!`);
      } else {
        const response = await axios.post(
          `${API}/family-trees/${currentTree.tree_id}/nodes`,
          personData,
          { withCredentials: true }
        );
        setCurrentTree({
          ...currentTree,
          nodes: [...(currentTree.nodes || []), response.data]
        });
        toast.success(`${newPerson.name} added to the tree!`);
      }

      resetModal();
    } catch (error) {
      console.error('Error with person:', error);
      toast.error(isEditingPerson ? 'Failed to update person' : 'Failed to add person');
    }
  };

  const resetModal = () => {
    setNewPerson({ name: '', location: '', photo: '', relation: '' });
    setShowAddPerson(false);
    setSelectedNode(null);
    setAddRelationType(null);
    setIsEditingPerson(false);
    setEditingNodeId(null);
  };

  const openEditPersonModal = (node) => {
    setNewPerson({
      name: node.name,
      location: node.location || '',
      photo: node.photo || '',
      relation: node.relation || ''
    });
    setEditingNodeId(node.node_id);
    setIsEditingPerson(true);
    setSelectedNode(node);
    
    // Determine relation type from stored relation
    if (isParentRelation(node.relation)) setAddRelationType('parent');
    else if (isChildRelation(node.relation)) setAddRelationType('child');
    else if (isSpouseRelation(node.relation)) setAddRelationType('spouse');
    else if (isSiblingRelation(node.relation)) setAddRelationType('sibling');
    else setAddRelationType(null);
    
    setShowAddPerson(true);
  };

  const deleteNode = async (nodeId) => {
    if (!window.confirm('Delete this person and all their branches?')) return;

    try {
      await axios.delete(`${API}/family-trees/${currentTree.tree_id}/nodes/${nodeId}`, { withCredentials: true });

      const removeNodeAndDescendants = (nodes, targetId) => {
        const childIds = nodes.filter(n => n.parent_node_id === targetId).map(n => n.node_id);
        let remaining = nodes.filter(n => n.node_id !== targetId);
        childIds.forEach(childId => {
          remaining = removeNodeAndDescendants(remaining, childId);
        });
        return remaining;
      };

      setCurrentTree({
        ...currentTree,
        nodes: removeNodeAndDescendants(currentTree.nodes, nodeId)
      });

      toast.success('Removed from tree');
    } catch (error) {
      console.error('Error deleting node:', error);
      toast.error('Failed to delete');
    }
  };

  const openAddPersonModal = (node, relationType) => {
    setSelectedNode(node);
    setAddRelationType(relationType);
    setIsEditingPerson(false);
    setEditingNodeId(null);
    setNewPerson({ name: '', location: '', photo: '', relation: '' });
    setShowAddPerson(true);
  };

  const copyShareLink = () => {
    const link = `${window.location.origin}/tree-view/${currentTree.tree_id}`;
    navigator.clipboard.writeText(link);
    toast.success('Link copied!');
  };

  const sendInvite = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Please enter an email');
      return;
    }

    try {
      await axios.post(`${API}/family-trees/${currentTree.tree_id}/invite`, { email: inviteEmail }, { withCredentials: true });
      toast.success(`Invitation sent to ${inviteEmail}`);
      setInviteEmail('');
      setShowInviteModal(false);
    } catch (error) {
      toast.error('Failed to send invite');
    }
  };

  const getColorForNode = (colorIndex) => FAMILY_COLORS[colorIndex % FAMILY_COLORS.length];

  // Get relation options based on type
  const getRelationOptions = () => {
    switch (addRelationType) {
      case 'parent': return PARENT_RELATIONS;
      case 'child': return CHILD_RELATIONS;
      case 'spouse': return SPOUSE_RELATIONS;
      case 'sibling': return SIBLING_RELATIONS;
      default: return ['Self', 'Me'];
    }
  };

  // Build the tree graph structure
  const buildTreeGraph = () => {
    const nodes = currentTree?.nodes || [];
    const nodeMap = new Map();
    
    // Initialize map
    nodes.forEach(node => {
      nodeMap.set(node.node_id, {
        ...node,
        spouses: [],
        siblings: [],
        children: [],
        parents: []
      });
    });

    // Build relationships
    nodes.forEach(node => {
      if (node.parent_node_id && nodeMap.has(node.parent_node_id)) {
        const parentNode = nodeMap.get(node.parent_node_id);
        
        if (isSpouseRelation(node.relation)) {
          parentNode.spouses.push(node.node_id);
        } else if (isSiblingRelation(node.relation)) {
          parentNode.siblings.push(node.node_id);
        } else if (isChildRelation(node.relation) || node.branch_direction === 'bottom') {
          parentNode.children.push(node.node_id);
        } else if (isParentRelation(node.relation) || node.branch_direction === 'top') {
          parentNode.parents.push(node.node_id);
        }
      }
    });

    return nodeMap;
  };

  // Render a person card
  const PersonCard = ({ node, showControls = true }) => {
    const color = getColorForNode(node.color_index || 0);
    
    return (
      <div 
        className={`relative p-3 rounded-xl ${color.bg} border-2 ${color.border} shadow-md min-w-[130px] max-w-[150px] ${isEditMode && showControls ? 'group' : ''}`}
        data-testid={`person-card-${node.node_id}`}
      >
        {/* Relation label */}
        {node.relation && node.relation !== 'Self' && node.relation !== 'Me' && (
          <div className={`absolute -top-3 left-1/2 transform -translate-x-1/2 px-2 py-0.5 rounded-full text-[10px] font-medium border
            ${isSpouseRelation(node.relation) ? 'bg-rose-100 border-rose-300 text-rose-700' : 
              isSiblingRelation(node.relation) ? 'bg-blue-100 border-blue-300 text-blue-700' : 
              isParentRelation(node.relation) ? 'bg-amber-100 border-amber-300 text-amber-700' :
              'bg-green-100 border-green-300 text-green-700'}`}
          >
            {node.relation}
          </div>
        )}

        {/* Edit/Delete buttons */}
        {isEditMode && showControls && (
          <div className="absolute -top-2 -right-2 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
            <button
              onClick={(e) => { e.stopPropagation(); openEditPersonModal(node); }}
              className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center hover:bg-blue-600 shadow-sm"
              title="Edit person"
              data-testid={`edit-btn-${node.node_id}`}
            >
              <Pencil className="w-3 h-3" strokeWidth={2} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); deleteNode(node.node_id); }}
              className="w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 shadow-sm"
              title="Delete person"
              data-testid={`delete-btn-${node.node_id}`}
            >
              <Trash2 className="w-3 h-3" strokeWidth={2} />
            </button>
          </div>
        )}

        {/* Photo */}
        {node.photo ? (
          <img 
            src={node.photo} 
            alt={node.name}
            className="w-12 h-12 rounded-full object-cover mx-auto mb-2 border-2 border-white shadow-sm"
          />
        ) : (
          <div className={`w-12 h-12 rounded-full ${color.bg} border-2 ${color.border} flex items-center justify-center mx-auto mb-2`}>
            <User className={`w-6 h-6 ${color.text}`} strokeWidth={1.5} />
          </div>
        )}

        {/* Name */}
        <p className={`text-center font-serif font-medium text-sm ${color.text} truncate`}>
          {node.name}
        </p>

        {/* Location */}
        {node.location && (
          <p className="text-center text-[10px] text-muted-foreground flex items-center justify-center mt-1">
            <MapPin className="w-2.5 h-2.5 mr-0.5 flex-shrink-0" strokeWidth={1.5} />
            <span className="truncate">{node.location}</span>
          </p>
        )}
      </div>
    );
  };

  // Add buttons component for a node
  const AddButtons = ({ node }) => {
    if (!isEditMode) return null;
    
    const nodeData = buildTreeGraph().get(node.node_id) || node;
    const hasSpouse = nodeData.spouses?.length > 0;
    
    return (
      <div className="flex flex-col items-center gap-1 mt-2" data-testid={`add-buttons-${node.node_id}`}>
        <div className="flex gap-1 flex-wrap justify-center">
          {/* Add Parent */}
          <button
            onClick={() => openAddPersonModal(node, 'parent')}
            className="px-2 py-1 text-[10px] rounded-full bg-amber-100 border border-amber-300 text-amber-700 hover:bg-amber-200 transition-colors flex items-center"
            data-testid={`add-parent-btn-${node.node_id}`}
          >
            <Plus className="w-3 h-3 mr-0.5" strokeWidth={2} />
            Parent
          </button>
          
          {/* Add Spouse (only if no spouse) */}
          {!hasSpouse && (
            <button
              onClick={() => openAddPersonModal(node, 'spouse')}
              className="px-2 py-1 text-[10px] rounded-full bg-rose-100 border border-rose-300 text-rose-700 hover:bg-rose-200 transition-colors flex items-center"
              data-testid={`add-spouse-btn-${node.node_id}`}
            >
              <Plus className="w-3 h-3 mr-0.5" strokeWidth={2} />
              Spouse
            </button>
          )}
          
          {/* Add Sibling */}
          <button
            onClick={() => openAddPersonModal(node, 'sibling')}
            className="px-2 py-1 text-[10px] rounded-full bg-blue-100 border border-blue-300 text-blue-700 hover:bg-blue-200 transition-colors flex items-center"
            data-testid={`add-sibling-btn-${node.node_id}`}
          >
            <Plus className="w-3 h-3 mr-0.5" strokeWidth={2} />
            Sibling
          </button>
          
          {/* Add Child */}
          <button
            onClick={() => openAddPersonModal(node, 'child')}
            className="px-2 py-1 text-[10px] rounded-full bg-green-100 border border-green-300 text-green-700 hover:bg-green-200 transition-colors flex items-center"
            data-testid={`add-child-btn-${node.node_id}`}
          >
            <Plus className="w-3 h-3 mr-0.5" strokeWidth={2} />
            Child
          </button>
        </div>
      </div>
    );
  };

  // Render a family unit (person with spouse and their children)
  const FamilyUnit = ({ nodeId, nodeMap, renderedNodes, level = 0 }) => {
    if (renderedNodes.has(nodeId)) return null;
    renderedNodes.add(nodeId);
    
    const nodeData = nodeMap.get(nodeId);
    if (!nodeData) return null;

    // Get parents
    const parents = nodeData.parents.filter(id => !renderedNodes.has(id));
    
    // Get spouse
    const spouseIds = nodeData.spouses.filter(id => !renderedNodes.has(id));
    const spouseId = spouseIds[0];
    const spouseData = spouseId ? nodeMap.get(spouseId) : null;
    
    // Mark spouse as rendered
    if (spouseId) renderedNodes.add(spouseId);
    
    // Get siblings
    const siblingIds = nodeData.siblings.filter(id => !renderedNodes.has(id));
    
    // Get children (from both this node and spouse)
    let childrenIds = [...nodeData.children];
    if (spouseData) {
      childrenIds = [...childrenIds, ...spouseData.children];
    }
    childrenIds = [...new Set(childrenIds)].filter(id => !renderedNodes.has(id));

    return (
      <div className="flex flex-col items-center" data-testid={`family-unit-${nodeId}`}>
        {/* Parents section */}
        {parents.length > 0 && (
          <div className="flex flex-col items-center mb-4">
            <div className="flex items-center gap-4">
              {parents.map(parentId => (
                <FamilyUnit 
                  key={parentId} 
                  nodeId={parentId} 
                  nodeMap={nodeMap} 
                  renderedNodes={renderedNodes}
                  level={level + 1}
                />
              ))}
            </div>
            {/* Vertical connector from parents */}
            <div className="w-0.5 h-4 bg-gray-300 mt-2"></div>
          </div>
        )}

        {/* Main row: Siblings + Person + Spouse */}
        <div className="flex items-start gap-6">
          {/* Siblings on left (no connecting line, just space) */}
          {siblingIds.map(sibId => {
            const sibData = nodeMap.get(sibId);
            if (!sibData) return null;
            renderedNodes.add(sibId);
            
            // Sibling's spouse
            const sibSpouseId = sibData.spouses.filter(id => !renderedNodes.has(id))[0];
            const sibSpouseData = sibSpouseId ? nodeMap.get(sibSpouseId) : null;
            if (sibSpouseId) renderedNodes.add(sibSpouseId);
            
            // Sibling's children
            let sibChildrenIds = [...sibData.children];
            if (sibSpouseData) sibChildrenIds = [...sibChildrenIds, ...sibSpouseData.children];
            sibChildrenIds = [...new Set(sibChildrenIds)].filter(id => !renderedNodes.has(id));
            
            return (
              <div key={sibId} className="flex flex-col items-center">
                <div className="flex items-center">
                  <div className="flex flex-col items-center">
                    <PersonCard node={sibData} />
                    <AddButtons node={sibData} />
                  </div>
                  
                  {/* Sibling's spouse with heart connector */}
                  {sibSpouseData && (
                    <>
                      <div className="flex flex-col items-center mx-2">
                        <Heart className="w-4 h-4 text-rose-500 fill-rose-500" />
                        <div className="w-8 h-0.5 bg-rose-300"></div>
                      </div>
                      <div className="flex flex-col items-center">
                        <PersonCard node={sibSpouseData} />
                        <AddButtons node={sibSpouseData} />
                      </div>
                    </>
                  )}
                </div>
                
                {/* Sibling's children */}
                {sibChildrenIds.length > 0 && (
                  <div className="flex flex-col items-center mt-3">
                    <div className="w-0.5 h-4 bg-gray-300"></div>
                    {sibChildrenIds.length > 1 && (
                      <div className="h-0.5 bg-gray-300" style={{ width: `${sibChildrenIds.length * 80}px` }}></div>
                    )}
                    <div className="flex gap-4 mt-1">
                      {sibChildrenIds.map(childId => (
                        <FamilyUnit 
                          key={childId} 
                          nodeId={childId} 
                          nodeMap={nodeMap} 
                          renderedNodes={renderedNodes}
                          level={level + 1}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Main person + spouse */}
          <div className="flex flex-col items-center">
            <div className="flex items-center">
              {/* Main person */}
              <div className="flex flex-col items-center">
                <PersonCard node={nodeData} />
                {!spouseData && <AddButtons node={nodeData} />}
              </div>
              
              {/* Spouse with heart connector */}
              {spouseData && (
                <>
                  <div className="flex flex-col items-center mx-3">
                    <Heart className="w-5 h-5 text-rose-500 fill-rose-500" />
                    <div className="w-10 h-0.5 bg-rose-300 mt-1"></div>
                  </div>
                  <div className="flex flex-col items-center">
                    <PersonCard node={spouseData} />
                  </div>
                </>
              )}
            </div>
            
            {/* Add buttons for couple */}
            {spouseData && (
              <div className="flex gap-2 mt-2">
                <AddButtons node={nodeData} />
              </div>
            )}
            
            {/* Children section */}
            {childrenIds.length > 0 && (
              <div className="flex flex-col items-center mt-4">
                {/* Vertical line down */}
                <div className="w-0.5 h-4 bg-gray-300"></div>
                
                {/* Horizontal line for multiple children */}
                {childrenIds.length > 1 && (
                  <div className="h-0.5 bg-gray-300" style={{ width: `${Math.max(120, childrenIds.length * 100)}px` }}></div>
                )}
                
                {/* Children */}
                <div className="flex gap-6 mt-1">
                  {childrenIds.map(childId => {
                    const childData = nodeMap.get(childId);
                    if (!childData) return null;
                    
                    return (
                      <div key={childId} className="flex flex-col items-center">
                        {childrenIds.length > 1 && <div className="w-0.5 h-3 bg-gray-300"></div>}
                        <FamilyUnit 
                          nodeId={childId} 
                          nodeMap={nodeMap} 
                          renderedNodes={renderedNodes}
                          level={level + 1}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render the tree using useMemo to prevent StrictMode double-render issues
  const treeContent = useMemo(() => {
    const nodeMap = buildTreeGraph();
    const rootNodes = (currentTree?.nodes || []).filter(n => !n.parent_node_id);
    
    if (rootNodes.length === 0) {
      return null; // Will handle empty state in renderTree
    }
    
    // Pre-compute which nodes to render to avoid mutation during render
    const processNode = (nodeId, processed = new Set()) => {
      if (processed.has(nodeId)) return null;
      processed.add(nodeId);
      
      const nodeData = nodeMap.get(nodeId);
      if (!nodeData) return null;
      
      return {
        nodeId,
        nodeData,
        spouseData: nodeData.spouses[0] ? (() => {
          const spouseId = nodeData.spouses[0];
          if (processed.has(spouseId)) return null;
          processed.add(spouseId);
          return nodeMap.get(spouseId);
        })() : null,
        siblings: nodeData.siblings.map(sibId => {
          if (processed.has(sibId)) return null;
          return processNode(sibId, processed);
        }).filter(Boolean),
        parents: nodeData.parents.map(parentId => {
          if (processed.has(parentId)) return null;
          return processNode(parentId, processed);
        }).filter(Boolean),
        children: nodeData.children.map(childId => {
          if (processed.has(childId)) return null;
          return processNode(childId, processed);
        }).filter(Boolean),
      };
    };
    
    const processed = new Set();
    return rootNodes.map(node => processNode(node.node_id, processed)).filter(Boolean);
  }, [currentTree?.nodes]);
  
  // Render the tree
  const renderTree = () => {
    const rootNodes = (currentTree?.nodes || []).filter(n => !n.parent_node_id);
    
    if (rootNodes.length === 0) {
      return (
        <div 
          data-testid="add-first-person"
          className={`text-center p-12 border-2 border-dashed rounded-2xl transition-all ${isEditMode ? 'cursor-pointer border-primary/30 hover:border-primary hover:bg-primary/5' : 'border-gray-200'}`}
          onClick={() => isEditMode && openAddPersonModal(null, null)}
        >
          {isEditMode ? (
            <>
              <Plus className="w-16 h-16 text-primary/50 mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-xl font-serif text-foreground mb-2">Click to add first person</p>
              <p className="text-sm font-sans text-muted-foreground">Start building your family tree</p>
            </>
          ) : (
            <>
              <TreePine className="w-16 h-16 text-gray-300 mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-xl font-serif text-gray-400 mb-2">No family members yet</p>
              <p className="text-sm font-sans text-muted-foreground">Click "Edit Tree" to start adding family members</p>
            </>
          )}
        </div>
      );
    }
    
    // Helper to render a processed node
    const renderProcessedNode = (processedNode, level = 0) => {
      if (!processedNode) return null;
      const { nodeId, nodeData, spouseData, siblings, parents, children } = processedNode;
      
      // Get sibling spouse data
      const getSiblingSpouseData = (sibling) => {
        const sibNodeData = sibling?.nodeData;
        if (!sibNodeData) return null;
        const sibSpouseId = sibNodeData.spouses?.[0];
        if (!sibSpouseId) return null;
        return buildTreeGraph().get(sibSpouseId);
      };
      
      return (
        <div className="flex flex-col items-center" data-testid={`family-unit-${nodeId}`} key={nodeId}>
          {/* Parents section */}
          {parents.length > 0 && (
            <div className="flex flex-col items-center mb-4">
              <div className="flex items-center gap-4">
                {parents.map(parent => renderProcessedNode(parent, level + 1))}
              </div>
              <div className="w-0.5 h-4 bg-gray-300 mt-2"></div>
            </div>
          )}

          {/* Main row: Siblings + Person + Spouse */}
          <div className="flex items-start gap-6">
            {/* Siblings */}
            {siblings.map(sibling => {
              if (!sibling) return null;
              const sibSpouseData = getSiblingSpouseData(sibling);
              
              return (
                <div key={sibling.nodeId} className="flex flex-col items-center">
                  <div className="flex items-center">
                    <div className="flex flex-col items-center">
                      <PersonCard node={sibling.nodeData} />
                      <AddButtons node={sibling.nodeData} />
                    </div>
                    
                    {sibSpouseData && (
                      <>
                        <div className="flex flex-col items-center mx-2">
                          <Heart className="w-4 h-4 text-rose-500 fill-rose-500" />
                          <div className="w-8 h-0.5 bg-rose-300"></div>
                        </div>
                        <div className="flex flex-col items-center">
                          <PersonCard node={sibSpouseData} />
                          <AddButtons node={sibSpouseData} />
                        </div>
                      </>
                    )}
                  </div>
                  
                  {/* Sibling's children */}
                  {sibling.children.length > 0 && (
                    <div className="flex flex-col items-center mt-3">
                      <div className="w-0.5 h-4 bg-gray-300"></div>
                      {sibling.children.length > 1 && (
                        <div className="h-0.5 bg-gray-300" style={{ width: `${sibling.children.length * 80}px` }}></div>
                      )}
                      <div className="flex gap-4 mt-1">
                        {sibling.children.map(child => renderProcessedNode(child, level + 1))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Main person + spouse */}
            <div className="flex flex-col items-center">
              <div className="flex items-center">
                <div className="flex flex-col items-center">
                  <PersonCard node={nodeData} />
                  {!spouseData && <AddButtons node={nodeData} />}
                </div>
                
                {spouseData && (
                  <>
                    <div className="flex flex-col items-center mx-3">
                      <Heart className="w-5 h-5 text-rose-500 fill-rose-500" />
                      <div className="w-10 h-0.5 bg-rose-300 mt-1"></div>
                    </div>
                    <div className="flex flex-col items-center">
                      <PersonCard node={spouseData} />
                    </div>
                  </>
                )}
              </div>
              
              {spouseData && (
                <div className="flex gap-2 mt-2">
                  <AddButtons node={nodeData} />
                </div>
              )}
              
              {/* Children section */}
              {children.length > 0 && (
                <div className="flex flex-col items-center mt-4">
                  <div className="w-0.5 h-4 bg-gray-300"></div>
                  {children.length > 1 && (
                    <div className="h-0.5 bg-gray-300" style={{ width: `${Math.max(120, children.length * 100)}px` }}></div>
                  )}
                  <div className="flex gap-6 mt-1">
                    {children.map(child => (
                      <div key={child.nodeId} className="flex flex-col items-center">
                        {children.length > 1 && <div className="w-0.5 h-3 bg-gray-300"></div>}
                        {renderProcessedNode(child, level + 1)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    };
    
    return (
      <div className="inline-block" data-testid="tree-nodes-container">
        {treeContent?.map(node => renderProcessedNode(node))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-3">
            <TreePine className="w-8 h-8 text-primary" strokeWidth={1.5} />
            <h1 className="text-2xl font-serif font-semibold text-foreground">Sarmoh</h1>
          </Link>
          
          {/* Center Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            <Button 
              variant="ghost"
              onClick={() => navigate('/create-memorial')}
              className="text-foreground-muted hover:text-primary hover:bg-primary/5 rounded-full px-5 py-2 font-sans text-sm"
            >
              <Heart className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Memorial Tribute
            </Button>
            <Button 
              variant="ghost"
              onClick={() => navigate('/tree-builder')}
              className="text-primary bg-primary/10 rounded-full px-5 py-2 font-sans text-sm"
            >
              <TreePine className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Family Tree
            </Button>
          </div>

          {/* User Menu */}
          {user && (
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-2 px-3 py-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="w-4 h-4 text-primary" />
                  </div>
                )}
                <span className="text-sm font-sans text-foreground hidden sm:block">{user.name}</span>
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              </button>
              
              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white border border-border rounded-lg shadow-lg py-1 z-50">
                  <button
                    onClick={() => { setShowUserMenu(false); navigate('/settings'); }}
                    className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-gray-50 flex items-center"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Account Settings
                  </button>
                  <button
                    onClick={handleLogout}
                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-50 flex items-center"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </nav>

      {/* Tree List View */}
      {showTreeList && (
        <div className="max-w-4xl mx-auto px-6 py-12" data-testid="my-trees-list">
          <div className="text-center mb-8">
            <TreePine className="w-12 h-12 text-primary mx-auto mb-4" strokeWidth={1.5} />
            <h1 className="text-4xl font-serif tracking-tight text-foreground mb-2">My Family Trees</h1>
            <p className="text-base font-sans text-foreground-muted">Manage and build your family trees</p>
          </div>

          <div className="flex justify-center mb-8">
            <Button
              onClick={() => { setIsEditingTreeName(false); setShowNameModal(true); }}
              data-testid="create-tree-btn"
              className="bg-primary text-primary-foreground hover:bg-primary-hover rounded-full px-8 py-3 font-serif"
            >
              <Plus className="w-5 h-5 mr-2" strokeWidth={1.5} />
              Create New Family Tree
            </Button>
          </div>

          {trees.length > 0 ? (
            <div className="grid gap-4" data-testid="trees-grid">
              {trees.map(tree => (
                <div
                  key={tree.tree_id}
                  data-testid={`tree-card-${tree.tree_id}`}
                  className="bg-white border border-border shadow-card rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => { navigate(`/tree-builder/${tree.tree_id}`); fetchTree(tree.tree_id); }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <TreePine className="w-10 h-10 text-primary" strokeWidth={1.5} />
                      <div>
                        <h3 className="text-xl font-serif text-foreground">{tree.name}</h3>
                        <p className="text-sm text-muted-foreground">{tree.node_count || 0} members</p>
                      </div>
                    </div>
                    <ArrowLeft className="w-5 h-5 text-muted-foreground rotate-180" strokeWidth={1.5} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-paper border border-border rounded-lg p-12 text-center">
              <TreePine className="w-16 h-16 text-primary/30 mx-auto mb-4" strokeWidth={1.5} />
              <h3 className="text-xl font-serif text-foreground mb-2">No family trees yet</h3>
              <p className="text-foreground-muted font-sans mb-6">Create your first family tree to get started</p>
            </div>
          )}
        </div>
      )}

      {/* Tree Builder View */}
      {!showTreeList && currentTree && (
        <div className="flex flex-col h-[calc(100vh-73px)]">
          {/* Tree Header */}
          <div className="bg-paper border-b border-border px-6 py-4">
            <div className="max-w-7xl mx-auto flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => { setShowTreeList(true); setIsEditMode(false); }}
                  className="text-primary hover:underline font-sans text-sm"
                  data-testid="back-to-trees"
                >
                  <ArrowLeft className="w-4 h-4 inline mr-1" /> My Trees
                </button>
                <h2 className="text-2xl font-serif text-foreground">{currentTree.name}</h2>
                {isEditMode && (
                  <button
                    onClick={() => { setTreeName(currentTree.name); setIsEditingTreeName(true); setShowNameModal(true); }}
                    className="text-muted-foreground hover:text-primary"
                  >
                    <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                )}
              </div>
              <div className="flex items-center space-x-2">
                {isEditMode ? (
                  <Button
                    onClick={() => setIsEditMode(false)}
                    data-testid="save-tree-btn"
                    className="bg-green-600 text-white hover:bg-green-700 rounded-full px-6 py-2 text-sm font-serif"
                  >
                    <Eye className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Save & View
                  </Button>
                ) : (
                  <Button
                    onClick={() => setIsEditMode(true)}
                    data-testid="edit-tree-btn"
                    className="bg-primary text-primary-foreground hover:bg-primary-hover rounded-full px-6 py-2 text-sm font-serif"
                  >
                    <Pencil className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Edit Tree
                  </Button>
                )}
                <Button
                  onClick={() => setShowInviteModal(true)}
                  variant="outline"
                  className="border border-primary/20 text-primary hover:bg-primary/5 rounded-full px-4 py-2 text-sm font-serif"
                >
                  <Mail className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Invite
                </Button>
                <Button
                  onClick={() => setShowShareModal(true)}
                  variant="outline"
                  className="border border-primary/20 text-primary hover:bg-primary/5 rounded-full px-4 py-2 text-sm font-serif"
                >
                  <Share2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Share
                </Button>
              </div>
            </div>
          </div>

          {/* Mode Indicator */}
          {isEditMode && (
            <div className="bg-amber-50 border-b border-amber-200 px-6 py-2">
              <p className="text-center text-sm text-amber-700 font-sans">
                <Pencil className="w-4 h-4 inline mr-2" strokeWidth={1.5} />
                Edit Mode - Click buttons to add family members, hover over cards to edit/delete
              </p>
            </div>
          )}

          {/* Canvas */}
          <div className="flex-1 overflow-auto p-8" data-testid="tree-canvas">
            <div className="min-h-full flex items-center justify-center">
              {renderTree()}
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Person Modal */}
      {showAddPerson && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={resetModal}>
          <div className="bg-white rounded-lg shadow-floating max-w-md w-full" data-testid="add-person-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bg-paper border-b border-border p-6 flex justify-between items-center">
              <h2 className="text-xl font-serif text-foreground">
                {isEditingPerson ? 'Edit Person' : selectedNode ? `Add ${addRelationType ? addRelationType.charAt(0).toUpperCase() + addRelationType.slice(1) : 'Relative'}` : 'Add First Person'}
              </h2>
              <button onClick={resetModal} className="text-muted-foreground hover:text-foreground">
                <X className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Relation Type */}
              <div>
                <Label className="font-sans text-foreground mb-2 block">
                  Relation Type {addRelationType && <span className="text-muted-foreground">({addRelationType})</span>}
                </Label>
                <Select
                  value={newPerson.relation}
                  onValueChange={(value) => setNewPerson({ ...newPerson, relation: value })}
                >
                  <SelectTrigger className="w-full bg-white" data-testid="relation-select">
                    <SelectValue placeholder="Select relation type" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border shadow-lg z-[100]">
                    {getRelationOptions().map((rel) => (
                      <SelectItem key={rel} value={rel}>{rel}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="font-sans text-foreground mb-2 block">Name *</Label>
                <Input
                  data-testid="person-name-input"
                  value={newPerson.name}
                  onChange={(e) => setNewPerson({ ...newPerson, name: e.target.value })}
                  placeholder="Enter name"
                  className="bg-transparent border border-border focus:border-primary rounded-lg px-4 py-2 font-sans"
                />
              </div>

              <div>
                <Label className="font-sans text-foreground mb-2 block">Location (Optional)</Label>
                <Input
                  data-testid="person-location-input"
                  value={newPerson.location}
                  onChange={(e) => setNewPerson({ ...newPerson, location: e.target.value })}
                  placeholder="City, Country"
                  className="bg-transparent border border-border focus:border-primary rounded-lg px-4 py-2 font-sans"
                />
              </div>

              <div>
                <Label className="font-sans text-foreground mb-2 block">Photo (Optional)</Label>
                {newPerson.photo ? (
                  <div className="flex items-center space-x-4">
                    <img src={newPerson.photo} alt="Preview" className="w-16 h-16 rounded-full object-cover" />
                    <Button variant="outline" onClick={() => setNewPerson({ ...newPerson, photo: '' })} className="text-sm">
                      Remove
                    </Button>
                  </div>
                ) : (
                  <div>
                    <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" id="person-photo" />
                    <label htmlFor="person-photo">
                      <Button
                        type="button"
                        variant="outline"
                        className="border border-primary/20 text-primary hover:bg-primary/5 rounded-lg px-4 py-2 font-sans cursor-pointer"
                        onClick={() => document.getElementById('person-photo').click()}
                      >
                        <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} />
                        Upload Photo
                      </Button>
                    </label>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button variant="outline" onClick={resetModal} className="border border-border rounded-full px-6 py-2 font-serif">
                  Cancel
                </Button>
                <Button
                  data-testid="add-person-submit"
                  onClick={addPerson}
                  className="bg-primary text-primary-foreground hover:bg-primary-hover rounded-full px-6 py-2 font-serif"
                >
                  {isEditingPerson ? 'Update' : 'Add to Tree'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Name Tree Modal */}
      {showNameModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => { setShowNameModal(false); setIsEditingTreeName(false); }}>
          <div className="bg-white rounded-lg shadow-floating max-w-md w-full" data-testid="name-tree-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bg-paper border-b border-border p-6">
              <h2 className="text-xl font-serif text-foreground">
                {isEditingTreeName ? 'Rename Family Tree' : 'Name Your Family Tree'}
              </h2>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <Label className="font-sans text-foreground mb-2 block">Tree Name</Label>
                <Input
                  data-testid="tree-name-input"
                  value={treeName}
                  onChange={(e) => setTreeName(e.target.value)}
                  placeholder="e.g., Gupta Khandan, Sharma Parivar"
                  className="bg-transparent border border-border focus:border-primary rounded-lg px-4 py-2 font-sans"
                />
              </div>

              <div className="flex flex-wrap gap-2">
                {['Gupta Khandan', 'Sharma Parivar', 'Singh Family', 'My Family'].map(suggestion => (
                  <button
                    key={suggestion}
                    onClick={() => setTreeName(suggestion)}
                    className="px-3 py-1 bg-paper border border-border rounded-full text-sm font-sans text-foreground-muted hover:border-primary"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button variant="outline" onClick={() => { setShowNameModal(false); setIsEditingTreeName(false); }} className="border border-border rounded-full px-6 py-2 font-serif">
                  Cancel
                </Button>
                <Button
                  data-testid="name-tree-submit"
                  onClick={createNewTree}
                  className="bg-primary text-primary-foreground hover:bg-primary-hover rounded-full px-6 py-2 font-serif"
                >
                  {isEditingTreeName ? 'Update Name' : 'Create Tree'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowShareModal(false)}>
          <div className="bg-white rounded-lg shadow-floating max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="bg-paper border-b border-border p-6 flex justify-between items-center">
              <h2 className="text-xl font-serif text-foreground">Share Tree</h2>
              <button onClick={() => setShowShareModal(false)} className="text-muted-foreground hover:text-foreground">
                <X className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <Label className="font-sans text-foreground mb-2 block">View-Only Link</Label>
                <div className="flex items-center space-x-2">
                  <Input
                    readOnly
                    value={`${window.location.origin}/tree-view/${currentTree?.tree_id}`}
                    className="bg-paper border border-border rounded-lg px-4 py-2 font-mono text-sm flex-1"
                  />
                  <Button onClick={copyShareLink} className="bg-primary text-primary-foreground hover:bg-primary-hover rounded-lg px-4 py-2">
                    Copy
                  </Button>
                </div>
              </div>

              <div className="pt-4 border-t border-border">
                <Button
                  variant="outline"
                  className="w-full border border-primary/20 text-primary hover:bg-primary/5 rounded-full py-3 font-serif"
                  onClick={() => toast.info('PDF export coming soon!')}
                >
                  <FileDown className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Download as PDF
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowInviteModal(false)}>
          <div className="bg-white rounded-lg shadow-floating max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="bg-paper border-b border-border p-6 flex justify-between items-center">
              <h2 className="text-xl font-serif text-foreground">Invite Contributors</h2>
              <button onClick={() => setShowInviteModal(false)} className="text-muted-foreground hover:text-foreground">
                <X className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-sm font-sans text-foreground-muted">
                Invite family members to help build this tree together.
              </p>
              <div>
                <Label className="font-sans text-foreground mb-2 block">Email Address</Label>
                <Input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="family@example.com"
                  className="bg-transparent border border-border focus:border-primary rounded-lg px-4 py-2 font-sans"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button variant="outline" onClick={() => setShowInviteModal(false)} className="border border-border rounded-full px-6 py-2 font-serif">
                  Cancel
                </Button>
                <Button onClick={sendInvite} className="bg-primary text-primary-foreground hover:bg-primary-hover rounded-full px-6 py-2 font-serif">
                  <Mail className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Send Invite
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
